// Campaign detail — overview, edit (shared form), activate/disable/delete,
// per-user recipients monitoring, and blacklist management.
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  fetchCampaign,
  fetchCampaignRecipients,
  fetchCampaignBlacklist,
  updateCampaign,
  setCampaignStatus,
  deleteCampaign,
  addCampaignBlacklist,
  removeCampaignBlacklist,
  keys,
} from "@/lib/queries";
import type { Campaign, CampaignCreateInput } from "@/lib/queries";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Skeleton } from "@/components/ui/Skeleton";
import { CurrencyAmount, CurrencyBadge } from "@/components/ui/CurrencyAmount";
import { ConfirmModal } from "@/components/ui/ConfirmModal";
import { CampaignForm } from "@/components/campaigns/CampaignForm";
import { CampaignFundingPanel } from "@/components/campaigns/CampaignFundingPanel";
import { ClaimHistoryCard } from "@/components/campaigns/ClaimHistoryCard";
import { RecipientsCard } from "@/components/campaigns/RecipientsCard";
import {
  CREDIT_TYPE_LABEL,
  FREQUENCY_LABEL,
  WEEKDAY_LABELS,
  campaignUserLabel,
  campaignUserSupport,
  creditTypeOf,
  frequencyOf,
  timingOf,
  timingSummary,
} from "@/lib/campaigns";
import { fmtDate, fmtRelative } from "@/lib/format";

export const Route = createFileRoute("/_app/campaigns/$id")({
  component: CampaignDetailPage,
});

type Pending = "activate" | "disable" | "delete" | null;

function CampaignDetailPage() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const detail = useQuery({ queryKey: keys.campaignDetail(id), queryFn: () => fetchCampaign(id) });
  const recipients = useQuery({
    queryKey: keys.campaignRecipients(id, 50, 0),
    queryFn: () => fetchCampaignRecipients(id, { limit: 50, offset: 0 }),
  });
  const blacklist = useQuery({
    queryKey: keys.campaignBlacklist(id),
    queryFn: () => fetchCampaignBlacklist(id),
  });

  const [editOpen, setEditOpen] = useState(false);
  const [editErr, setEditErr] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending>(null);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: keys.campaigns });
    qc.invalidateQueries({ queryKey: keys.campaignDetail(id) });
  };

  const update = useMutation({
    mutationFn: (input: CampaignCreateInput) => updateCampaign(id, input),
    onSuccess: () => {
      setEditErr(null);
      setEditOpen(false);
      invalidate();
    },
    onError: (e) => setEditErr((e as Error).message),
    meta: { successMessage: "Campaign saved." },
  });

  const status = useMutation({
    mutationFn: (s: "ACTIVE" | "DISABLED") => setCampaignStatus(id, s),
    onSuccess: () => {
      setPending(null);
      invalidate();
    },
    meta: { successMessage: "Campaign status updated." },
  });

  const remove = useMutation({
    mutationFn: () => deleteCampaign(id),
    onSuccess: () => {
      setPending(null);
      qc.invalidateQueries({ queryKey: keys.campaigns });
      navigate({ to: "/campaigns" });
    },
    meta: { successMessage: "Campaign deleted." },
  });

  const [blUser, setBlUser] = useState("");
  const [blReason, setBlReason] = useState("");
  const addBl = useMutation({
    mutationFn: () => addCampaignBlacklist(id, blUser.trim(), blReason.trim()),
    onSuccess: () => {
      setBlUser("");
      setBlReason("");
      qc.invalidateQueries({ queryKey: keys.campaignBlacklist(id) });
    },
    meta: { successMessage: "User added to blocklist." },
  });
  const removeBl = useMutation({
    mutationFn: (userId: string) => removeCampaignBlacklist(id, userId),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.campaignBlacklist(id) }),
    meta: { successMessage: "User removed from blocklist." },
  });

  const c = detail.data;
  const isActive = c?.status === "ACTIVE";

  return (
    <div className="space-y-6">
      <div>
        <Link to="/campaigns" className="text-2xs text-muted hover:text-fg">
          ← back to campaigns
        </Link>
      </div>

      {detail.isLoading && <Skeleton className="h-40" />}
      {c && (
        <>
          <div className="card relative overflow-hidden">
            <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="font-display text-2xl tracking-tight text-fg">{c.name}</h1>
                  <Badge tone={isActive ? "success" : c.status === "DRAFT" ? "warning" : "muted"}>
                    {c.status}
                  </Badge>
                  <Badge tone={creditTypeOf(c) === "VISIT" ? "accent" : "muted"}>
                    {CREDIT_TYPE_LABEL[creditTypeOf(c)]}
                  </Badge>
                  <Badge tone="muted">{timingSummary(c)}</Badge>
                  <CurrencyBadge currency={c.currency} />
                </div>
                {c.description && <div className="mt-1 text-xs text-muted">{c.description}</div>}
                <div className="mt-0.5 font-mono text-2xs text-muted">{c.campaign_id}</div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button variant="ghost" onClick={() => setEditOpen((s) => !s)}>
                  {editOpen ? "Close edit" : "Edit"}
                </Button>
                {isActive ? (
                  <Button variant="danger" onClick={() => setPending("disable")}>
                    Disable
                  </Button>
                ) : (
                  <Button variant="primary" onClick={() => setPending("activate")}>
                    Activate
                  </Button>
                )}
                <Button variant="danger" onClick={() => setPending("delete")}>
                  Delete
                </Button>
              </div>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi
              label="Reward"
              value={<CurrencyAmount currency={c.currency} value={c.reward_amount_minor} />}
            />
            <Kpi
              label="Credited total"
              value={<CurrencyAmount currency={c.currency} value={c.credited_total_minor} />}
            />
            <Kpi label="Recipients" value={`${recipients.data?.total ?? 0}`} />
            <Kpi
              label="Per-user limit"
              value={
                timingOf(c) === "ONCE"
                  ? "Once per user"
                  : c.per_user_limit > 0
                    ? `${c.per_user_limit}× max`
                    : "Unlimited"
              }
            />
          </div>

          {editOpen && (
            <Card>
              <CardHeader className="border-b border-border">
                <CardTitle>Edit campaign</CardTitle>
              </CardHeader>
              <CardBody className="pt-5">
                <CampaignForm
                  mode="edit"
                  initial={c}
                  submitting={update.isPending}
                  error={editErr}
                  submitLabel="Save changes"
                  onSubmit={(input) => update.mutate(input)}
                />
              </CardBody>
            </Card>
          )}

          <div className="grid gap-6 lg:grid-cols-3">
            <div className="space-y-6 lg:col-span-2">
              <RecipientsCard campaign={c} />

              {/* SM-440: claim history — requested vs credited with the
                  capped-credit outcome per claim. */}
              <ClaimHistoryCard campaign={c} />

              {/* Blacklist */}
              <Card>
                <CardHeader className="border-b border-border">
                  <CardTitle>Blacklist</CardTitle>
                  <span className="text-2xs text-muted">{blacklist.data?.length ?? 0}</span>
                </CardHeader>
                <CardBody className="space-y-4 pt-5">
                  <div className="flex flex-wrap items-end gap-3">
                    <div className="min-w-[220px] flex-1">
                      <Input
                        label="User ID"
                        value={blUser}
                        onChange={(e) => setBlUser(e.target.value)}
                        placeholder="ULID…"
                      />
                    </div>
                    <div className="min-w-[220px] flex-1">
                      <Input
                        label="Reason (optional)"
                        value={blReason}
                        onChange={(e) => setBlReason(e.target.value)}
                      />
                    </div>
                    <Button
                      onClick={() => blUser.trim() && addBl.mutate()}
                      loading={addBl.isPending}
                      disabled={!blUser.trim()}
                    >
                      Block
                    </Button>
                  </div>
                  {(blacklist.data?.length ?? 0) === 0 ? (
                    <p className="rounded-lg border border-dashed border-border bg-surface-2 px-4 py-5 text-center text-2xs text-muted">
                      No users are blacklisted from this campaign.
                    </p>
                  ) : (
                    <ul className="divide-y divide-border/60 overflow-hidden rounded-lg border border-border">
                      {blacklist.data!.map((b) => (
                        <li key={b.user_id} className="flex items-center gap-3 px-3 py-2.5">
                          <div className="min-w-0 flex-1">
                            <div className="font-mono text-2xs text-fg">{b.user_id}</div>
                            {b.reason && <div className="mt-0.5 text-2xs text-muted">{b.reason}</div>}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeBl.mutate(b.user_id)}
                            disabled={removeBl.isPending}
                          >
                            Remove
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardBody>
              </Card>
            </div>

            {/* Details sidebar */}
            <div className="space-y-6">
              <Card>
                <CardHeader className="border-b border-border">
                  <CardTitle>Details</CardTitle>
                </CardHeader>
                <CardBody className="space-y-3 pt-5">
                  {creditTypeOf(c) === "VISIT" && c.image_url && (
                    <img
                      src={c.image_url}
                      alt={c.name}
                      className="mb-1 w-full rounded-lg border border-border object-cover"
                    />
                  )}
                  <Field k="Credit type" v={CREDIT_TYPE_LABEL[creditTypeOf(c)]} />
                  {creditTypeOf(c) === "VISIT" ? (
                    <Field k="Popup title" v={c.popup_title || "Reward received"} />
                  ) : (
                    <Field
                      k="Audience"
                      v={c.audience === "ACTIVE_SINCE" ? `Active ${c.audience_days ?? "?"}d` : "All users"}
                    />
                  )}
                  <Field k="Timing" v={timingOf(c) === "ONCE" ? "Once" : FREQUENCY_LABEL[frequencyOf(c)]} />
                  {timingOf(c) === "SCHEDULED" && (
                    <Field k="Time of day" v={`${c.time_of_day ?? "—"} (${c.timezone})`} />
                  )}
                  {c.schedule_kind === "WEEKLY" && (
                    <Field
                      k="Weekdays"
                      v={(c.weekdays ?? []).map((d) => WEEKDAY_LABELS[d]).join(", ") || "—"}
                    />
                  )}
                  {creditTypeOf(c) === "AUTO" && (
                    <Field k="Next run" v={c.next_run_at ? fmtRelative(c.next_run_at) : "—"} />
                  )}
                  <Field k="Starts" v={fmtDate(c.starts_at)} />
                  <Field k="Ends" v={c.ends_at ? fmtDate(c.ends_at) : "—"} />
                  <Field k="Created" v={fmtDate(c.created_at)} />
                </CardBody>
              </Card>

              {/* SM-152: funding account + coverage. Renders nothing
                  while transfer funding is dark. */}
              <CampaignFundingPanel campaign={c} />
            </div>
          </div>
        </>
      )}

      <ConfirmModal
        open={pending === "activate"}
        tone="primary"
        title="Activate this campaign?"
        description="Require-visit campaigns credit eligible users on their next signed-in visit; Auto-credit campaigns start firing on schedule."
        confirmLabel="Activate"
        cancelLabel="Keep as-is"
        loading={status.isPending}
        onConfirm={() => status.mutate("ACTIVE")}
        onCancel={() => setPending(null)}
      />
      <ConfirmModal
        open={pending === "disable"}
        tone="danger"
        title="Disable this campaign?"
        description="It stops showing / firing immediately. Existing receipts are kept."
        confirmLabel="Disable"
        cancelLabel="Keep active"
        loading={status.isPending}
        onConfirm={() => status.mutate("DISABLED")}
        onCancel={() => setPending(null)}
      />
      <ConfirmModal
        open={pending === "delete"}
        tone="danger"
        title="Delete this campaign?"
        description="Soft-deletes the campaign (receipts + history are preserved). It disappears from the list and stops firing."
        confirmLabel="Delete campaign"
        cancelLabel="Keep"
        loading={remove.isPending}
        onConfirm={() => remove.mutate()}
        onCancel={() => setPending(null)}
      />
    </div>
  );
}

function Kpi({ label, value, sub }: { label: string; value: React.ReactNode; sub?: string }) {
  return (
    <div className="card">
      <div className="text-2xs font-semibold uppercase tracking-wide text-muted">{label}</div>
      <div className="num mt-1.5 text-xl font-bold tracking-tight text-fg">{value}</div>
      {sub && <div className="mt-1 text-2xs text-muted">{sub}</div>}
    </div>
  );
}

function Field({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 text-xs">
      <span className="text-muted">{k}</span>
      <span className="text-right text-fg">{v}</span>
    </div>
  );
}
